import type { GuidedSetupStep } from '../components/guided-setup/guided-setup-steps';

/**
 * Guided-tour steps for the **Auto-scan after import** tab (`/settings/scan-policy/auto-scan`).
 *
 * Lives here beside `policy-schedule-tour-steps.ts` rather than beside its page, following
 * `risks-tour-steps.ts`: the registry imports every step set, and keeping them in `pages/` keeps
 * that import list flat.
 *
 * **Every `anchor` is the value of a `data-tour-step` ATTRIBUTE on a real element, never a CSS
 * selector.** A step whose anchor is absent from the DOM is SKIPPED rather than rendered pointing at
 * nothing — so on a deployment where the surface 404s (backend flag down) only the intro renders.
 *
 * Copy discipline: no step asserts a state the platform cannot observe. The tour does not promise
 * that a scan WILL run — that depends on the saved policy, which the page shows.
 *
 * The tour is HOST-owned (`GuidedTourHostComponent`): the permanent "Show me around" trigger it
 * renders on every authenticated route is the on-demand re-open path
 * (`.claude/rules/new-section-onboarding-coverage.md` R2), so the page renders no trigger of its
 * own — the same shape as the Scheduled scans tab.
 */

/** Tour id — must appear in `UserTourController.ALLOWED_TOUR_IDS` (<= 32 chars). */
export const AUTO_SCAN_TOUR_ID = 'auto-scan';

export const AUTO_SCAN_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'as-intro',
    title: 'What runs after a repository is first imported',
    anchor: null,
    body: [
      'This tab decides which scans start automatically the first time a repository is imported ' +
        'into a workspace — a single add, a batch, a monorepo import or a GitHub import all count. ' +
        'Re-analysing, rescanning or adding another branch of an existing repository never does.',
      'Nothing runs after import until an organization administrator turns this on. The default ' +
        'is off, and the page says so rather than implying a scan that will not happen.',
    ],
  },
  {
    id: 'as-org-card',
    title: 'The organization default',
    anchor: 'auto-scan-org-card',
    body: [
      'One policy for the whole organization: whether auto-scan is on, and which scans it runs. ' +
        'The switch asks before it writes, because turning this on means every future import ' +
        'starts real scans — some of them metered.',
      'Only an organization administrator can change it. Everyone else sees the same page ' +
        'read-only, which is deliberate: a developer importing a repository is entitled to know ' +
        'what will happen to it.',
    ],
  },
  {
    id: 'as-domains',
    title: 'Scans, modes, and Hunt last',
    anchor: 'auto-scan-domains',
    body: [
      'Each scan domain has its own mode list, published by the server — only modes a full ' +
        'repository scan can run are offered, never pull-request-only ones. FAST is preselected; ' +
        'a mode that spends AI budget, reaches the network or verifies credentials against their ' +
        'providers is labelled as such beside its name.',
      'Exploit Hunt is always the final stage. It starts for a repository only after every other ' +
        'selected scan for that repository has completed successfully; a failed prerequisite ' +
        'blocks it and the reason is recorded rather than the hunt running on unscanned code.',
    ],
  },
  {
    id: 'as-overrides',
    title: 'Workspace overrides and where an answer came from',
    anchor: 'auto-scan-overrides',
    body: [
      'A workspace can carry its own policy instead of inheriting the organization default. ' +
        'The list shows which ones do, and the preview below it resolves the effective answer for ' +
        'any workspace, naming its source — organization default, workspace override, or off.',
      'The same sentence appears read-only in the import wizards, so the person importing a ' +
        'repository sees what will run before they confirm.',
    ],
  },
  {
    id: 'as-dispatches',
    title: 'What actually happened after each import',
    anchor: 'auto-scan-dispatches',
    body: [
      'Every import the policy acted on is recorded here: how many scans were planned, queued ' +
        'and skipped, and where the Hunt stage stands — waiting, launched, or blocked with the ' +
        'domains that blocked it named.',
      'A blocked Hunt can be retried by an administrator once the prerequisite scans have ' +
        'succeeded. Retrying while one is still unsuccessful is refused, and the refusal says why.',
    ],
  },
];
